import { useProfileTranslations } from "@/hooks/use-profile-translations";
import { FaUnlock } from "react-icons/fa6";
import imgLogo from "@/assets/ig-logo-2.png";
import { useTranslation } from "react-i18next";

import { useRef } from "react";

export function HoneypotUnlock() {
  const { i18n } = useTranslation();
  const { tProfile, TransCommon, tCommon } = useProfileTranslations("checkout");

  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const priceInfo = {
    pt: { currency: "R$", value: "47", originalValue: "117,00" },
    en: { currency: "$", value: "10.00", originalValue: "19.99" },
    es: { currency: "$", value: "49.000", originalValue: "97.000" },
    mx: { currency: "MX$", value: "247", originalValue: "497" },
    eeuu: { currency: "$", value: "17", originalValue: "49" },
    ar: { currency: "AR$", value: "14700", originalValue: "30000" },
  };

  const currentLang = i18n.language.substring(0, 2);
  const price =
    priceInfo[currentLang as keyof typeof priceInfo] || priceInfo.pt;

  const getCheckoutUrl = () => {
    let baseUrl;
    switch (currentLang) {
      case "en":
        baseUrl = "https://go.pepper.com.br/en/checkout";
        break;
      case "es":
        baseUrl = "https://global.mundpay.com/tbnuxoge0o";
        break;
      case "mx":
        baseUrl =
          "https://global.mundpay.com/tbnuxoge0o";
        break;
      case "eeuu":
        baseUrl =
          "https://global.mundpay.com/tbnuxoge0o";
        break;
      default:
        baseUrl = "https://go.pepper.com.br/7qfr5";
        break;
      case "ar":
        baseUrl = "https://global.mundpay.com/tbnuxoge0o";
        break;
    }

    const url = new URL(baseUrl);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      url.searchParams.append(key, value);
    });

    const leadId = localStorage.getItem("lead_id");

    if (leadId) {
      url.searchParams.append("src", leadId);
    }

    const base64EncodedUrl = btoa(url.toString());

    return `https://track.teagram.app/?url=${base64EncodedUrl}`;
  };

  const scrollToPaymentSection = () => {
    paymentSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <div className="px-6 flex flex-col items-center relative min-h-screen">
      <div className="w-full max-w-md mx-auto my-6">
        {/* Premium unlock banner with dark theme */}
        <div
          onClick={scrollToPaymentSection}
          className="relative w-full bg-gradient-to-r border-t-orange-500 from-neutral-900 to-black rounded-xl border border-orange-900/40 shadow-lg transition-transform hover:scale-[1.01] cursor-pointer"
        >
          {/* Top decorative bar */}
          {/* <div className="absolute rounded-xl top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 to-orange-500"></div> */}

          {/* Lock icon with improved styling */}
          <div className="absolute -top-5 left-1/2 -translate-x-1/2">
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-2.5 rounded-full shadow-lg ">
              <FaUnlock className="text-neutral-900 text-xl" />
            </div>
          </div>

          {/* Content with improved spacing */}
          <div className="pt-8 pb-5 px-5 text-center">
            <h2 className="font-bold text-2xl text-orange-400 leading-tight tracking-tight mb-2">
              {tProfile("unlockBanner.title")}
            </h2>
            <p className="text-neutral-300 mb-3">
              {tProfile("unlockBanner.subtitle")}
            </p>
            {/* Call to action button */}
          </div>
        </div>

        {/* Premium insights banner with dark theme */}
        <div className="mt-8 relative bg-gradient-to-br from-neutral-900 via-neutral-900/90 to-neutral-900 border border-orange-900/40 rounded-xl p-5">
          {/* Premium badge */}
          <div className="absolute -top-3 -right-1">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-neutral-900 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide shadow-md">
              {tCommon("premium")}
            </div>
          </div>

          <div className="flex items-center">
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-3 rounded-lg shadow-md mr-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-neutral-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>

            <div>
              <h3 className="font-bold text-orange-400 text-lg mb-1">
                {tCommon("andMuchMore")}
              </h3>
              <p className="text-neutral-300 text-sm">
                {tCommon("premiumInsights")}
              </p>
            </div>
          </div>

          {/* Horizontal indicators for additional content */}
          <div className="mt-4 flex items-center justify-center space-x-1.5">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full ${i < 3 ? "w-6 bg-orange-500" : "w-3 bg-orange-900/50"}`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-20 h-20 rounded-lg">
        <img
          src={imgLogo}
          alt="Logo"
          className="w-full h-full object-contain"
        />
      </div>

      <div className="text-center px-8 py-6">
        <p className="text-muted-foreground text-xs leading-relaxed">
          {tCommon("aboutService.line1")}
        </p>
        <p className="text-muted-foreground text-xs leading-relaxed mt-1">
          {tCommon("aboutService.line2")}
        </p>
      </div>

      <div className="flex flex-col items-center">
        <div className="text-5xl font-black text-foreground tracking-tighter">
          203.549
        </div>
        <div className="text-muted-foreground">
          {tCommon("profilesAnalyzed")}
        </div>
      </div>

      <div
        ref={paymentSectionRef}
        className="w-full bg-neutral-900/90 backdrop-blur-sm rounded-xl border border-neutral-800 overflow-hidden shadow-xl mt-6"
      >
        <div className="p-5">
          {/* Premium report header with improved typography */}
          <div className="flex flex-col mb-5">
            <h2 className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
              {tCommon("fullReport")}
            </h2>
            <p className="text-neutral-300 text-sm mt-1"></p>
          </div>

          {/* Premium pricing card */}
          <div className="mb-6 relative overflow-hidden">
            {/* Background pattern for premium feel */}
            <div className="absolute inset-0 opacity-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
              >
                <defs>
                  <pattern
                    id="dotGrid"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle cx="10" cy="10" r="1" fill="currentColor" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#dotGrid)" />
              </svg>
            </div>
            <div className="bg-gradient-to-br from-neutral-800/70 to-neutral-900/70 backdrop-blur-sm rounded-xl p-4 border border-amber-900/30 relative">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center mb-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-4 h-4 text-amber-400 mr-1.5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.75.75 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-xs text-amber-200 font-medium">
                      {tCommon("pricingInfo.from")}{" "}
                      <span className="line-through text-neutral-400">
                        {price.currency}
                        {price.originalValue}
                      </span>{" "}
                      {tCommon("pricingInfo.for")}:
                    </p>
                  </div>

                  <div className="flex items-baseline">
                    <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                      {price.currency}
                      {price.value.split(".")[0]}
                    </span>

                    <span className="text-2xl font-bold text-amber-500/90">
                      {price.value.includes(".")
                        ? "." + price.value.split(".")[1]
                        : ""}
                    </span>
                  </div>
                </div>

                {/* Animated discount badge */}
                <div className="relative">
                  <div className="absolute inset-0 bg-rose-500 rounded-full blur-sm opacity-20 animate-pulse"></div>
                  <div className="bg-gradient-to-br from-rose-600 to-rose-800 text-white text-center py-2 px-4 rounded-full transform rotate-3 shadow-lg border border-rose-500/30 relative">
                    <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent opacity-50"></div>
                    <p className="text-xs font-bold">
                      {tCommon("pricingInfo.discount")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button with premium styling */}

          <div className="relative mb-3">
            <div className="absolute inset-0 bg-green-400 rounded-xl blur-sm opacity-70 animate-pulse"></div>

            <a
              href={getCheckoutUrl()}
              className="relative block w-full text-center bg-gradient-to-b from-green-600 to-green-700 text-background py-4 px-4 rounded-xl font-extrabold text-lg transition duration-200 shadow-md"
            >
              <div className="flex flex-col items-center">
                <span className="uppercase tracking-wide text-base text-white">
                  {tCommon("ctaButton.primary")}
                </span>
                <span className="text-xs font-normal opacity-90 mt-0.5 text-white">
                  <TransCommon
                    keyName="ctaButton.secondary"
                    values={{ price: `${price.currency}${price.value}` }}
                  />
                </span>
              </div>
            </a>
          </div>

          {/* Improved trust badges */}
          <div className="flex items-center justify-center gap-4 mb-3 bg-gradient-to-br from-neutral-800/40 to-neutral-900/40 rounded-xl p-3 border border-neutral-800/50">
            <div className="flex items-center gap-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 text-amber-500"
              >
                <path
                  fillRule="evenodd"
                  d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.75.75 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs font-medium text-neutral-300">
                {tCommon("guarantees.warranty")}
              </p>
            </div>

            <div className="h-5 w-px bg-neutral-700"></div>

            <div className="flex items-center gap-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 text-amber-500"
              >
                <path
                  fillRule="evenodd"
                  d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs font-medium text-neutral-300">
                {tCommon("guarantees.discreet")}
              </p>
            </div>

            <div className="h-5 w-px bg-neutral-700"></div>

            <div className="flex items-center gap-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 text-amber-500"
              >
                <path d="M4.5 3.75a3 3 0 00-3 3v.75h21v-.75a3 3 0 00-3-3h-15z" />
                <path
                  fillRule="evenodd"
                  d="M22.5 9.75h-21v7.5a3 3 0 003 3h15a3 3 0 003-3v-7.5zm-18 3.75a.75.75 0 01.75-.75h6a.75.75 0 010 1.5h-6a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs font-medium text-neutral-300">
                {tCommon("guarantees.secure")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Social proof counter */}
      <div className="w-full bg-neutral-900/60 backdrop-blur-sm p-3 text-center rounded-lg mb-4 border border-neutral-800/80 mt-4">
        <div className="flex items-center justify-center gap-2">
          <div className="relative flex items-center justify-center w-2 h-2">
            <div className="absolute w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
            <div className="absolute w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
          <p className="text-xs font-medium text-green-300">
            <TransCommon keyName="peopleBought" values={{ count: 236 }} />
          </p>
        </div>
      </div>
    </div>
  );
}
